import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SearchableCombobox from "@/components/SearchableCombobox";
import { createWatch, updateWatch, lookupMovement } from "@/lib/api";
import { LISTING_CHANNELS } from "@/lib/listing";
import { errorMessage } from "@/lib/errors";
import { toast } from "sonner";

const EMPTY = {
  excel_id: "",
  brand: "", model: "", caliber: "", mfg: "", year_mfg: "", serial: "",
  case_number: "", size: "", movement_type: "", bph: "", jewels: "",
  lift_angle: "",
  shock: "",
  case_size: "", case_material: "", case_back_type: "",
  lug_width: "", band_width: "", band_material: "",
  status: "", purchase_price: "", all_in: "", purch_date: "",
  purchased_from: "", bin_location: "", purchase_link: "", sale_price: "",
  sale_date: "", listing_channel: "", listed_on: "", listing_price: "", ebay_order_id: "", ebay_item_id: "",
  parts: "", earnings: "", profit_loss: "", min_sale_price: "",
  notes: "",
};

const toNumOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const toStrOrNull = (v) => (v === "" || v === null || v === undefined ? null : String(v));

// Pricing model constants (kept in sync with backend core/shared.py).
// When editable Settings lands, we'll fetch these from /api/watches/pricing instead.
const PRICING = { ebayFee: 0.15, adFee: 0.08, markup: 0.25 };
function computeMinSalePrice(allIn, parts) {
  const a = Number(allIn) || 0;
  const p = Number(parts) || 0;
  const costs = a + p;
  if (costs <= 0) return null;
  const denom = 1 - PRICING.ebayFee - PRICING.adFee;
  if (denom <= 0) return null;
  return Math.round((costs * (1 + PRICING.markup) / denom) * 100) / 100;
}

/** Numeric input wrapped with a currency prefix so the user sees the $ sign
 *  next to money fields. Purely cosmetic — the stored value is still a plain
 *  number. */
function MoneyInput({ value, onChange, ...rest }) {
  return (
    <div className="relative">
      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#3A2A10] font-mono-data text-sm pointer-events-none">$</span>
      <Input
        type="number"
        step="0.01"
        value={value}
        onChange={onChange}
        className="dark-input pl-7"
        {...rest}
      />
    </div>
  );
}

export default function WatchFormSheet({ open, onOpenChange, watch, validation = {}, onSaved }) {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (watch) {
      const seed = { ...EMPTY };
      Object.keys(EMPTY).forEach((k) => {
        seed[k] = watch[k] === null || watch[k] === undefined ? "" : watch[k];
      });
      setData(seed);
    } else {
      setData(EMPTY);
    }
  }, [watch, open]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        excel_id: toNumOrNull(data.excel_id),
        brand: toStrOrNull(data.brand),
        model: toStrOrNull(data.model),
        caliber: toStrOrNull(data.caliber),
        mfg: toStrOrNull(data.mfg),
        year_mfg: toStrOrNull(data.year_mfg),
        serial: toStrOrNull(data.serial),
        case_number: toStrOrNull(data.case_number),
        size: toStrOrNull(data.size),
        movement_type: toStrOrNull(data.movement_type),
        bph: toNumOrNull(data.bph),
        jewels: toNumOrNull(data.jewels),
        lift_angle: toNumOrNull(data.lift_angle),
        shock: toStrOrNull(data.shock),
        case_size: toStrOrNull(data.case_size),
        case_material: toStrOrNull(data.case_material),
        case_back_type: toStrOrNull(data.case_back_type),
        lug_width: toStrOrNull(data.lug_width),
        band_width: toStrOrNull(data.band_width),
        band_material: toStrOrNull(data.band_material),
        status: toStrOrNull(data.status),
        purchase_price: toNumOrNull(data.purchase_price),
        all_in: toNumOrNull(data.all_in),
        purch_date: toStrOrNull(data.purch_date),
        purchased_from: toStrOrNull(data.purchased_from),
        bin_location: toStrOrNull(data.bin_location),
        purchase_link: toStrOrNull(data.purchase_link),
        sale_price: toNumOrNull(data.sale_price),
        sale_date: toStrOrNull(data.sale_date),
        listing_channel: toStrOrNull(data.listing_channel),
        listed_on: toStrOrNull(data.listed_on),
        listing_price: toNumOrNull(data.listing_price),
        ebay_order_id: toStrOrNull(data.ebay_order_id),
        ebay_item_id: toStrOrNull(data.ebay_item_id),
        parts: toNumOrNull(data.parts),
        earnings: toNumOrNull(data.earnings),
        profit_loss: toNumOrNull(data.profit_loss),
        min_sale_price: toNumOrNull(data.min_sale_price),
        notes: toStrOrNull(data.notes),
      };
      if (watch?.id) {
        await updateWatch(watch.id, payload);
        toast.success("Watch updated");
      } else {
        await createWatch(payload);
        toast.success("Watch added to vault");
      }
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to save watch"));
    } finally {
      setSaving(false);
    }
  };

  /**
   * When the user picks a caliber, look it up in the movements reference
   * table and pre-fill the movement spec fields. Only fills blanks —
   * preserves anything the user has already typed so we don't clobber
   * manual edits.
   */
  const onCaliberChange = async (newCaliber) => {
    setData((d) => ({ ...d, caliber: newCaliber }));
    if (!newCaliber || !newCaliber.trim()) return;
    try {
      const res = await lookupMovement(newCaliber.trim());
      if (!res?.found) return;
      const m = res.movement || {};
      // movement.jewels is free text ("17" or "7/17" or "17/21/25") while the
      // watch.jewels field is a single integer. Extract the FIRST run of
      // digits so compound forms still populate a sensible default.
      const jewelsMatch = m.jewels != null ? String(m.jewels).match(/\d+/) : null;
      const mJewelsNum = jewelsMatch ? Number(jewelsMatch[0]) : null;
      setData((d) => ({
        ...d,
        mfg: d.mfg || m.mvmnt_mfg || "",
        movement_type: d.movement_type || m.movement_type || "",
        size: d.size || m.size || "",
        jewels: (d.jewels === "" || d.jewels === null) && mJewelsNum !== null ? mJewelsNum : d.jewels,
        bph: d.bph === "" || d.bph === null ? (m.bph ?? "") : d.bph,
        lift_angle: (d.lift_angle === "" || d.lift_angle === null) && m.lift_angle != null ? m.lift_angle : d.lift_angle,
        shock: d.shock || m.protection || "",
      }));
      toast.success(`Pre-filled from movement ${newCaliber}`);
    } catch { /* silent — auto-fill is a convenience, not required */ }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#FBF3D9] border-l border-[#8B6A1F]/45 text-[#0A0604] overflow-y-auto"
        data-testid="watch-form-sheet"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl text-[#0A0604]">
            {watch ? "Edit Piece" : "New Piece"}
          </SheetTitle>
          <SheetDescription className="text-[#3A2A10] text-xs uppercase tracking-[0.25em]">
            {watch ? "Refine details" : "Catalogue a new acquisition"}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="watch-form">
          <Section title="General">
            <Row2>
              <Field label="ID (Excel row #)">
                <Input
                  type="number"
                  value={data.excel_id}
                  onChange={(e) => set("excel_id", e.target.value)}
                  className="dark-input font-mono-data"
                  placeholder={watch ? "" : "auto-assigned on save"}
                  data-testid="form-excel-id"
                />
              </Field>
              <Field label="Brand">
                <ComboSelect value={data.brand} onChange={(v) => set("brand", v)} options={validation["Maker"]} testid="form-brand" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Mvmnt MFG">
                <ComboSelect value={data.mfg} onChange={(v) => set("mfg", v)} options={validation["Mvmnt MFG"]} testid="form-mfg" />
              </Field>
              <Field label="Year / Era">
                <Input value={data.year_mfg} onChange={(e) => set("year_mfg", e.target.value)} className="dark-input" placeholder="e.g. 1960s, c.1955" data-testid="form-year" />
              </Field>
            </Row2>
            <Field label="Model / Notes">
              <Input value={data.model} onChange={(e) => set("model", e.target.value)} className="dark-input" data-testid="form-model" />
            </Field>
            <Row2>
              <Field label="Caliber">
                <ComboSelect value={data.caliber} onChange={onCaliberChange} options={validation["Caliber"]} testid="form-caliber" lookupOnly />
              </Field>
              <Field label="Serial Number">
                <Input value={data.serial} onChange={(e) => set("serial", e.target.value)} className="dark-input" data-testid="form-serial" />
              </Field>
            </Row2>
            <Field label="Case Number">
              <Input value={data.case_number} onChange={(e) => set("case_number", e.target.value)} className="dark-input" data-testid="form-case" />
            </Field>
          </Section>

          <Section title="Movement">
            <Row2>
              <Field label="Type">
                <ComboSelect value={data.movement_type} onChange={(v) => set("movement_type", v)} options={validation["Movement Type"]} testid="form-type" />
              </Field>
              <Field label="Size">
                <ComboSelect value={data.size} onChange={(v) => set("size", v)} options={validation["Size"]} testid="form-size" />
              </Field>
            </Row2>
            <Row2>
              <Field label="BPH">
                <ComboSelect value={data.bph ? String(data.bph) : ""} onChange={(v) => set("bph", v)} options={validation["Beat Rate"]} testid="form-bph" />
              </Field>
              <Field label="Jewels">
                <ComboSelect value={data.jewels !== "" ? String(data.jewels) : ""} onChange={(v) => set("jewels", v)} options={validation["Jewels"]} testid="form-jewels" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Lift Angle (°)">
                <Input
                  type="number"
                  step="0.1"
                  value={data.lift_angle}
                  onChange={(e) => set("lift_angle", e.target.value)}
                  className="dark-input"
                  data-testid="form-lift-angle"
                />
              </Field>
              <Field label="Shock System">
                <ComboSelect value={data.shock} onChange={(v) => set("shock", v)} options={validation["Shock Systems"]} testid="form-shock" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Status">
                <ComboSelect value={data.status} onChange={(v) => set("status", v)} options={validation["Status"]} testid="form-status" />
              </Field>
              <div />
            </Row2>
          </Section>

          <Section title="Case & Band">
            <Row2>
              <Field label="Case Size">
                <ComboSelect value={data.case_size} onChange={(v) => set("case_size", v)} options={validation["Case Size"]} testid="form-case-size" />
              </Field>
              <Field label="Case Material">
                <ComboSelect value={data.case_material} onChange={(v) => set("case_material", v)} options={validation["Case Material"]} testid="form-case-material" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Case Back">
                <ComboSelect value={data.case_back_type} onChange={(v) => set("case_back_type", v)} options={validation["Case Back Type"]} testid="form-case-back" />
              </Field>
              <Field label="Lug Width">
                <ComboSelect value={data.lug_width} onChange={(v) => set("lug_width", v)} options={validation["Lug Width"]} testid="form-lug-width" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Band Width">
                <ComboSelect value={data.band_width} onChange={(v) => set("band_width", v)} options={validation["Band Width"]} testid="form-band-width" />
              </Field>
              <Field label="Band Material">
                <ComboSelect value={data.band_material} onChange={(v) => set("band_material", v)} options={validation["Band Material"]} testid="form-band-material" />
              </Field>
            </Row2>
          </Section>

          <Section title="Location">
            <Row2>
              <Field label="Location">
                <ComboSelect value={data.bin_location} onChange={(v) => set("bin_location", v)} options={validation["Location"]} testid="form-drawer" />
              </Field>
              <Field label="Purchased From">
                <ComboSelect value={data.purchased_from} onChange={(v) => set("purchased_from", v)} options={validation["Purchase From"]} testid="form-from" />
              </Field>
            </Row2>
            <Field label="Purchase Link">
              <Input value={data.purchase_link} onChange={(e) => set("purchase_link", e.target.value)} className="dark-input" data-testid="form-link" />
            </Field>
          </Section>

          <Section title="Financials">
            <Row2>
              <Field label="Purchase Price">
                <MoneyInput value={data.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} data-testid="form-purchase" />
              </Field>
              <Field label="All In">
                <MoneyInput value={data.all_in} onChange={(e) => set("all_in", e.target.value)} data-testid="form-allin" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Purchase Date">
                <Input type="date" value={data.purch_date || ""} onChange={(e) => set("purch_date", e.target.value)} className="dark-input" data-testid="form-purchdate" />
              </Field>
              <Field label="Min Sale Price">
                <div className="flex gap-1.5 items-stretch">
                  <div className="relative flex-1">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-[#3A2A10] font-mono-data text-sm pointer-events-none">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      value={data.min_sale_price}
                      onChange={(e) => set("min_sale_price", e.target.value)}
                      className="dark-input pl-7"
                      data-testid="form-minsale"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const v = computeMinSalePrice(data.all_in, data.parts || watch?.parts);
                      if (v === null) {
                        toast.error("Set All In (and optional Parts) first");
                      } else {
                        set("min_sale_price", String(v));
                        toast.success(`Computed $${v.toFixed(2)}`);
                      }
                    }}
                    className="px-2 text-[10px] uppercase tracking-wider text-[#3A2A10] border border-[#8B6A1F]/45 rounded-sm hover:bg-[#2B2214]/[0.08]"
                    title="Auto-compute: (All In + Parts) × 1.25 / 0.77 — breakeven with 25% markup after 15% eBay + 8% ad fees"
                    data-testid="form-minsale-recalc"
                  >
                    auto
                  </button>
                </div>
                {(() => {
                  const suggested = computeMinSalePrice(data.all_in, data.parts || watch?.parts);
                  if (suggested === null) return null;
                  const current = Number(data.min_sale_price);
                  const differs = data.min_sale_price !== "" && Math.abs(current - suggested) > 0.005;
                  return (
                    <div className="text-[10px] text-[#3A2A10] font-mono-data mt-1">
                      Suggested: ${suggested.toFixed(2)}
                      {differs && <span className="text-rose-600"> · manual override</span>}
                    </div>
                  );
                })()}
              </Field>
            </Row2>
            <Row2>
              <Field label="Sale Price">
                <MoneyInput value={data.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="form-sale" />
              </Field>
              <Field label="Sale Date">
                <Input type="date" value={data.sale_date || ""} onChange={(e) => set("sale_date", e.target.value)} className="dark-input" data-testid="form-saledate" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Parts (auto-computed)">
                <div className="relative">
                  <Input
                    type="number"
                    value={data.parts ?? ""}
                    readOnly
                    disabled
                    className="dark-input opacity-70 cursor-not-allowed"
                    data-testid="form-parts"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-wider text-[#3A2A10]/70 font-mono-data pointer-events-none">
                    ledger
                  </span>
                </div>
              </Field>
              <Field label="Earnings">
                <MoneyInput value={data.earnings} onChange={(e) => set("earnings", e.target.value)} data-testid="form-earnings" />
              </Field>
            </Row2>
            <Field label="Profit / Loss (auto-computed)">
              <div className="relative">
                <Input
                  type="number"
                  value={data.profit_loss ?? ""}
                  readOnly
                  disabled
                  className="dark-input opacity-70 cursor-not-allowed"
                  data-testid="form-pl"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] uppercase tracking-wider text-[#3A2A10]/70 font-mono-data pointer-events-none">
                  derived
                </span>
              </div>
              <div className="text-[10px] text-[#3A2A10] mt-1 font-mono-data">
                earnings − all_in − parts
              </div>
            </Field>
          </Section>

          <Section title="Listing">
            <Row2>
              <Field label="Channel">
                <Select
                  value={data.listing_channel || "__none__"}
                  onValueChange={(v) => {
                    const newChannel = v === "__none__" ? "" : v;
                    setData((d) => {
                      const next = { ...d, listing_channel: newChannel };
                      // When the watch is first being listed (channel going
                      // from empty → a real channel) and the user hasn't set
                      // a listing price yet, pre-populate from the computed
                      // min-sale suggestion so they never list below breakeven
                      // by accident. Override remains manual.
                      const wasNotListed = !d.listing_channel;
                      const hasNoListingPrice = !d.listing_price || d.listing_price === "";
                      if (newChannel && wasNotListed && hasNoListingPrice) {
                        const suggested = d.min_sale_price && d.min_sale_price !== ""
                          ? Number(d.min_sale_price)
                          : computeMinSalePrice(d.all_in, d.parts || watch?.parts);
                        if (suggested && !Number.isNaN(suggested)) {
                          next.listing_price = String(suggested);
                          // Stamp the listed-on date to today if it's empty
                          if (!d.listed_on) {
                            next.listed_on = new Date().toISOString().slice(0, 10);
                          }
                        }
                      }
                      return next;
                    });
                  }}
                >
                  <SelectTrigger className="dark-input h-10" data-testid="form-listing-channel">
                    <SelectValue placeholder="Not listed" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F9F0D4] border-[#8B6A1F]/45 rounded-sm">
                    <SelectItem value="__none__">— Not listed —</SelectItem>
                    {LISTING_CHANNELS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Listed On">
                <Input type="date" value={data.listed_on || ""} onChange={(e) => set("listed_on", e.target.value)} className="dark-input" data-testid="form-listed-on" />
              </Field>
            </Row2>
            <Field label="Listing Price">
              <MoneyInput
                value={data.listing_price}
                onChange={(e) => set("listing_price", e.target.value)}
                placeholder="Pre-fills from Min Sale when you pick a channel"
                data-testid="form-listing-price"
              />
            </Field>
            <div className="text-[10px] text-[#3A2A10]">
              Auto-populates with the Min Sale price when you first pick a channel. Override any time.
            </div>
            <Field label="eBay Order ID">
              <Input
                value={data.ebay_order_id || ""}
                onChange={(e) => set("ebay_order_id", e.target.value)}
                placeholder="e.g. 22-13913-19191 — paste from your eBay order"
                className="dark-input font-mono-data"
                data-testid="form-ebay-order-id"
              />
            </Field>
            <Field label="eBay Item ID">
              <Input
                value={data.ebay_item_id || ""}
                onChange={(e) => set("ebay_item_id", e.target.value)}
                placeholder="e.g. 156123456789 — 12-digit eBay item number"
                className="dark-input font-mono-data"
                data-testid="form-ebay-item-id"
              />
            </Field>
            <div className="text-[10px] text-[#3A2A10]">
              A single eBay order can contain multiple watches. Order ID ties to the sale, Item ID uniquely identifies <em>this</em> watch — use Item ID as the canonical join key for per-watch analytics. Leave blank if not sold on eBay.
            </div>
          </Section>

          <Section title="Notes">
            <Textarea value={data.notes} onChange={(e) => set("notes", e.target.value)} className="dark-input min-h-24" data-testid="form-notes" />
          </Section>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#8B6A1F]/35 sticky bottom-0 bg-[#FBF3D9] -mx-6 px-6 pb-2 pt-4">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-[#3A2A10] hover:text-[#0A0604]" data-testid="form-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] uppercase tracking-wider text-xs rounded-sm" data-testid="form-submit">
              {saving ? "Saving…" : watch ? "Save Changes" : "Add to Vault"}
            </Button>
          </div>
        </form>

        <style>{`
          .dark-input {
            background-color: #F9F0D4 !important;
            border-color: rgba(139,106,31,0.25) !important;
            border-radius: 2px !important;
            color: #2B2214 !important;
          }
          .dark-input:focus, .dark-input:focus-visible {
            border-color: rgba(212,175,55,0.5) !important;
            outline: none !important;
            box-shadow: 0 0 0 1px rgba(212,175,55,0.3) !important;
          }
        `}</style>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#3A2A10] mb-4 pb-2 border-b border-[#8B6A1F]/35">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Row2({ children }) {
  return <div className="grid grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.2em] text-[#3A2A10] font-normal">{label}</Label>
      {children}
    </div>
  );
}

function ComboSelect({ value, onChange, options = [], testid, lookupOnly = false }) {
  // Delegate to SearchableCombobox so every dropdown gets a real text-filter
  // (huge UX win on iPhone for long lists like Caliber & Maker). lookupOnly
  // disables the "type custom" pencil — used for Caliber so users can't
  // silently create orphans that won't reconcile back to Movements.
  return (
    <SearchableCombobox
      value={value}
      onChange={onChange}
      options={options}
      testid={testid}
      lookupOnly={lookupOnly}
    />
  );
}
